import React, { useMemo } from "react";
import { ResourceEntity } from "../services/types/resourceEntity";
import Tooltip from "@/pages/taskpane/components/ui/Tooltip";
import { useTranslation } from "react-i18next";
import { formatDateToDayMonthYear } from "@/pages/taskpane/lib/utils/timeUtils";
import { useAppointmentStore, useOutlookStore } from "@/pages/taskpane/shared/stores";
import { EPermissionType } from "../services/types/enum";
import { AVAILABULITY_DESCRIPTION } from "@/pages/taskpane/shared/constants/permission";
import { handleSortDate, parseISOString, timeToMinutes } from "../ulti";
import { useFormatDateTime } from "@/pages/taskpane/shared/hooks";

type Props = {
  resource: ResourceEntity;
};

const AvailabilityBox = ({ resource }: Props) => {
  const { t } = useTranslation();
  const { recurringDates } = useOutlookStore();
  const { startDateTimeZone, endDateTimeZone } = useAppointmentStore();
  const isRecurring = useMemo(() => recurringDates.length > 0, [recurringDates]);
  const {
    total = 0,
    numberOfAvailable = 0,
    unAvailableDates = [],
    occurrenceCapacities = [],
  } = resource?.availability || {};

  const isInClosedHours = useMemo(() => {
    if (!resource?.openStartTime || !resource?.openEndTime || !startDateTimeZone || !endDateTimeZone) {
      return false;
    }

    try {
      const openStartParsed = parseISOString(resource.openStartTime);
      const openEndParsed = parseISOString(resource.openEndTime);
      const bookingStartParsed = parseISOString(startDateTimeZone);
      const bookingEndParsed = parseISOString(endDateTimeZone);

      const openStartMinutes = timeToMinutes(openStartParsed);
      const openEndMinutes = timeToMinutes(openEndParsed);
      const bookingStartMinutes = timeToMinutes(bookingStartParsed);
      const bookingEndMinutes = timeToMinutes(bookingEndParsed);

      const isStartOutside = bookingStartMinutes < openStartMinutes || bookingStartMinutes >= openEndMinutes;
      const isEndOutside = bookingEndMinutes <= openStartMinutes || bookingEndMinutes > openEndMinutes;

      return isStartOutside || isEndOutside;
    } catch (error) {
      console.warn("Failed to check closed hours:", error);
      return false;
    }
  }, [resource?.openStartTime, resource?.openEndTime, startDateTimeZone, endDateTimeZone]);

  const messageTooltip = useMemo(() => {
    const permissions = resource?.availability?.permissions(isRecurring);
    if (permissions && permissions?.length > 0) {
      const basicAvailabilityContent = (
        <AvailableOccurrencesContent
          numberOfAvailable={numberOfAvailable}
          total={total}
          unAvailableDates={unAvailableDates || []}
        />
      );

      // access denied
      if (
        permissions?.includes(EPermissionType.VRConfigWrong) ||
        permissions?.includes(EPermissionType.DeclinedByInPolicy)
      ) {
        return basicAvailabilityContent;
      }
      if (permissions?.includes(EPermissionType.PreCloseDate)) {
        return (
          <AvailableOccurrencesContent
            numberOfAvailable={numberOfAvailable}
            total={total}
            unAvailableDates={unAvailableDates || []}
            isClosed
          />
        );
      }

      // alert red
      if (permissions?.includes(EPermissionType.AllOccurrenceExceedCapacity)) {
        return <p>{t(AVAILABULITY_DESCRIPTION[EPermissionType.ExceedCapacity] ?? "")}</p>;
      }
      if (
        permissions?.includes(EPermissionType.DeclinedByOutPolicy) ||
        permissions?.includes(EPermissionType.NotAllowRecurringBooking)
      ) {
        return basicAvailabilityContent;
      }
      if (permissions?.includes(EPermissionType.CannotBookByExchangeByRule)) {
        return (
          <AvailableOccurrencesContent
            numberOfAvailable={numberOfAvailable}
            total={total}
            unAvailableDates={unAvailableDates || []}
            description={t(AVAILABULITY_DESCRIPTION[EPermissionType.CannotBookByExchangeByRule] ?? "")}
          />
        );
      }

      // alert yellow
      if (permissions?.includes(EPermissionType.CloseDate)) {
        return (
          <AvailableOccurrencesContent
            numberOfAvailable={numberOfAvailable}
            total={total}
            unAvailableDates={unAvailableDates || []}
            isClosed={true}
          />
        );
      }

      // alert blue
      if (permissions?.includes(EPermissionType.OccurrencceExceedCapacity)) {
        return (
          <AvailableOccurrencesContent
            numberOfAvailable={numberOfAvailable}
            total={total}
            unAvailableDates={unAvailableDates || []}
            occurrenceCapacities={occurrenceCapacities.filter((item) => item.remainingCapacity !== item.allowCapacity)}
            description={t(AVAILABULITY_DESCRIPTION[EPermissionType.OccurrencceExceedCapacity] ?? "")}
          />
        );
      }
      if (permissions?.includes(EPermissionType.OccurenciesWithConflicts)) {
        return basicAvailabilityContent;
      }
      if (permissions?.includes(EPermissionType.CanBookByExchangeByRule)) {
        return (
          <AvailableOccurrencesContent
            numberOfAvailable={numberOfAvailable}
            total={total}
            unAvailableDates={unAvailableDates || []}
            description={t(AVAILABULITY_DESCRIPTION[EPermissionType.CanBookByExchangeByRule] ?? "")}
          />
        );
      }

      // people
      if (permissions?.includes(EPermissionType.DelegateByInOutPolicy)) {
        return basicAvailabilityContent;
      }
    }

    // resource in closed hours
    if (isInClosedHours) {
      return <p>{t(AVAILABULITY_DESCRIPTION[EPermissionType.ResourceInClosedHours] ?? "")}</p>;
    }
    return (
      <AvailableOccurrencesContent
        numberOfAvailable={numberOfAvailable}
        total={total}
        unAvailableDates={unAvailableDates}
      />
    );
  }, [
    total,
    numberOfAvailable,
    isRecurring,
    unAvailableDates,
    occurrenceCapacities,
    isInClosedHours,
    resource?.availability,
    t,
  ]);

  return (
    <Tooltip position="top" message={<div className="text-xs">{messageTooltip}</div>}>
      <p className="text-xs">
        {t("Availability")}: {numberOfAvailable}/{total}
      </p>
    </Tooltip>
  );
};

export default AvailabilityBox;

const AvailableOccurrencesContent = ({
  numberOfAvailable,
  total,
  unAvailableDates,
  occurrenceCapacities,
  description,
  isClosed,
}: {
  numberOfAvailable: number;
  total: number;
  unAvailableDates: string[];
  occurrenceCapacities?: { occurrence: string; allowCapacity: number; remainingCapacity: number }[];
  description?: string;
  isClosed?: boolean;
}) => {
  const { t } = useTranslation();
  const { handleFormatDateWithSeparator } = useFormatDateTime();
  const ulContent = useMemo(() => {
    return (
      <>
        {unAvailableDates?.length > 0 || (occurrenceCapacities && occurrenceCapacities?.length > 0) ? (
          <p>{t("Unavailable Occurrences")}:</p>
        ) : null}
        <ul>
          {occurrenceCapacities && occurrenceCapacities?.length > 0
            ? occurrenceCapacities.map((item) => (
                <li key={item.occurrence}>
                  - {handleFormatDateWithSeparator(item.occurrence)} ({item.remainingCapacity}/{item.allowCapacity})
                </li>
              ))
            : unAvailableDates?.length > 0
            ? handleSortDate(unAvailableDates).map((date: string, index: number) => (
                <li key={`${date}-${index}`}>
                  - {handleFormatDateWithSeparator(date)} {isClosed ? `(${t("Closed")})` : ""}
                </li>
              ))
            : null}
        </ul>
      </>
    );
  }, [occurrenceCapacities, unAvailableDates, isClosed]);

  return (
    <>
      <p>{description ? description : ""}</p>
      <div>
        {t("Available occurrences of the series")}: {numberOfAvailable}/{total}
        {ulContent}
      </div>
    </>
  );
};
